import React, { useState, useRef, memo } from 'react';
import { useSelector } from 'react-redux';
import { YMaps, Map, Placemark, ZoomControl, FullscreenControl } from 'react-yandex-maps';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import Marker from 'react-google-maps/lib/components/Marker';

// Components
import { GMaps } from '../../../../components/GoogleMap';

// Icons
import pin from './assets/pin.svg';
import location from './assets/location.svg';
import courier from './assets/courier.svg';

// State
import { mapLocale as mapLocaleSelector } from '../../../../state/modules/map/selectors';

// Config
import config from '../../../../config';

// Styles
import styles from './OrderMap.styl';

const cx = classNames.bind(styles);

function OrderMap(props) {
  const {
    className,
    orderData: { delivery_type, restaurant_point, delivery_point, courier_point, order_status },
  } = props;

  const isRealPhone = useSelector(state => state.responsive.isRealPhone);
  const zoom = useSelector(state => state.city.userCity.zoom) || 10;
  const mapLocale = useSelector(mapLocaleSelector);

  const map = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const handleApiAvaliable = () => {
    setIsLoaded(true);
  };

  // const getGoogleMapBoundArray = () => {
  //   if (delivery_type === 'delivery' && delivery_point && restaurant_point) {
  //     const restaurantBound = [
  //       parseFloat(restaurant_point[0]),
  //       parseFloat(restaurant_point[1]),
  //     ];
  //     const deliveryBound = [parseFloat(delivery_point[0]), parseFloat(delivery_point[1])];

  //     const leftBottom = new google.maps.LatLng(
  //       Math.min(restaurantBound[0], deliveryBound[0]),
  //       Math.min(restaurantBound[1], deliveryBound[1]),
  //     );

  //     const rightTop = new google.maps.LatLng(
  //       Math.max(restaurantBound[0], deliveryBound[0]),
  //       Math.max(restaurantBound[1], deliveryBound[1]),
  //     );

  //     return new google.maps.LatLngBounds(leftBottom, rightTop);
  //   }

  //   return null;
  // };

  const getGoogleMapRef = ref => {
    if (ref) {
      map.current = ref;

      // const bounds = getGoogleMapBoundArray();
      const bounds = null;

      if (bounds && delivery_type === 'delivery') {
        map.current.fitBounds(bounds);
        map.current.panToBounds(bounds);
      }

      handleApiAvaliable();
    }
  };

  const getYandexMapBoundArray = () => {
    let yandexMapBoundArray = null;

    if (delivery_type === 'delivery' && restaurant_point && delivery_point) {
      const restaurantBound = [parseFloat(restaurant_point[1]), parseFloat(restaurant_point[0])];
      const deliveryBound = [parseFloat(delivery_point[1]), parseFloat(delivery_point[0])];

      const leftBottom = [
        Math.min(restaurantBound[0], deliveryBound[0]),
        Math.min(restaurantBound[1], deliveryBound[1]),
      ];

      const rightTop = [
        Math.max(restaurantBound[0], deliveryBound[0]),
        Math.max(restaurantBound[1], deliveryBound[1]),
      ];

      yandexMapBoundArray = [leftBottom, rightTop];
    }

    return yandexMapBoundArray;
  };

  const getYandexMapRef = ref => {
    if (ref) {
      map.current = ref;

      const bounds = map.current.getBounds();

      if (bounds && delivery_type === 'delivery') {
        map.current.setBounds(bounds, { checkZoomRange: true, zoomMargin: 1 });
      }

      if (isRealPhone) {
        map.current.behaviors.disable('drag');
      }
    }
  };

  return (
    <div className={cx('OrderMap', className)}>
      {!isLoaded && <div className={cx('OrderMap__preloader')} />}
      {config.legal === 'pl' ? (
        <GMaps
          getMapRef={getGoogleMapRef}
          loadingElement={<div />}
          containerElement={<div style={{ width: '100%', height: '100%' }} />}
          mapElement={<div style={{ width: '100%', height: '100%', zIndex: 2 }} />}
          defaultZoom={zoom}
          defaultCenter={[restaurant_point[0], restaurant_point[1]]}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            gestureHandling: isRealPhone ? 'none' : 'auto',
          }}
        >
          <Marker
            position={{
              lng: parseFloat(restaurant_point[0]),
              lat: parseFloat(restaurant_point[1]),
            }}
            icon={pin}
            draggable={false}
          />
          {delivery_type === 'delivery' && delivery_point && (
            <Marker
              position={{
                lng: parseFloat(delivery_point[0]),
                lat: parseFloat(delivery_point[1]),
              }}
              icon={location}
              draggable={false}
            />
          )}
        </GMaps>
      ) : (
        <YMaps query={{ lang: mapLocale, apikey: config.yandexApiKey }} enterprise>
          <Map
            width="100%"
            height="100%"
            instanceRef={getYandexMapRef}
            onLoad={handleApiAvaliable}
            state={{
              zoom,
              center: [restaurant_point[1], restaurant_point[0]],
              bounds: getYandexMapBoundArray(),
            }}
          >
            <Placemark
              geometry={[restaurant_point[1], restaurant_point[0]]}
              options={{
                iconLayout: 'default#image',
                iconImageHref: pin,
                iconImageSize: [32, 32],
                iconImageOffset: [-16, -32],
                cursor: 'default',
              }}
            />
            {delivery_type === 'delivery' && (
              <>
                {/* это значок КУДА доставлять */}
                {delivery_point && (
                  <Placemark
                    geometry={[delivery_point[1], delivery_point[0]]}
                    options={{
                      iconLayout: 'default#image',
                      iconImageHref: location,
                      iconImageSize: [26, 26],
                      iconImageOffset: [-13, -26],
                      cursor: 'default',
                    }}
                  />
                )}
                {/* а это передвижной курьер */}
                {Number(order_status) === 4 && courier_point && (
                  <Placemark
                    geometry={[courier_point[1], courier_point[0]]}
                    options={{
                      iconLayout: 'default#image',
                      iconImageHref: courier,
                      iconImageSize: [26, 26],
                      iconImageOffset: [-13, -26],
                      cursor: 'default',
                    }}
                  />
                )}
              </>
            )}
            <FullscreenControl />
            <ZoomControl />
          </Map>
        </YMaps>
      )}
    </div>
  );
}

// ререндерим компонент только при перемещении курьера
export default memo(OrderMap, (prevProps, nextProps) => {
  if (Number(nextProps.order_status) === 4 && prevProps.courier_point !== nextProps.courier_point) {
    return false;
  }

  return true;
});

OrderMap.defaultProps = {
  className: '',
};

OrderMap.propTypes = {
  className: PropTypes.string,
  orderData: PropTypes.object.isRequired,
};
